import React from "react";
import { useEventsData } from "../contexts/EventsContext";
import { getSeasonStatus } from "../lib/eventsUtils";
import { useCountdown } from "../hooks/useCountdown";
import {
  Gamepad2,
  Trophy,
  Crosshair,
  Target,
  Clock,
  CheckCircle2,
  Radio,
} from "lucide-react";

// -----------------------------------------------------------------------
// خريطة الأيقونات + الألوان لكل موسم
// -----------------------------------------------------------------------
const ICONS = {
  trophy: Trophy,
  crosshair: Crosshair,
  target: Target,
};

const ACCENT_STYLES = {
  gold: {
    ring: "shadow-[0_10px_30px_-12px_rgba(201,169,106,0.45)]",
    border: "border-[#C9A96A]/40",
    iconBg:
      "bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128]",
    text: "text-[#E5CB94]",
    badgeBg:
      "bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128]",
    stripe: "from-[#C9A96A]/40 via-[#C9A96A]/10 to-transparent",
  },
  red: {
    ring: "shadow-[0_10px_30px_-12px_rgba(220,38,38,0.35)]",
    border: "border-red-500/35",
    iconBg: "bg-gradient-to-b from-red-500 to-red-700 text-white",
    text: "text-red-200",
    badgeBg: "bg-red-500 text-white",
    stripe: "from-red-500/40 via-red-500/10 to-transparent",
  },
  purple: {
    ring: "shadow-[0_10px_30px_-12px_rgba(139,92,246,0.35)]",
    border: "border-violet-500/35",
    iconBg: "bg-gradient-to-b from-violet-500 to-violet-700 text-white",
    text: "text-violet-200",
    badgeBg: "bg-violet-500 text-white",
    stripe: "from-violet-500/40 via-violet-500/10 to-transparent",
  },
};

const STATUS_META = {
  upcoming: {
    label: "قادم",
    dot: "bg-sky-400",
    pill: "bg-sky-500/15 border-sky-500/40 text-sky-200",
    Icon: Clock,
    cta: "يبدأ بعد",
  },
  live: {
    label: "متوفر الآن",
    dot: "bg-emerald-400 animate-pulse",
    pill: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
    Icon: Radio,
    cta: "ينتهي بعد",
  },
  ended: {
    label: "انتهى",
    dot: "bg-white/30",
    pill: "bg-white/[0.04] border-white/10 text-white/50",
    Icon: CheckCircle2,
    cta: "الموسم منتهٍ",
  },
};

// -----------------------------------------------------------------------
// عنصر قيمة العدّ التنازلي (يوم/ساعة/دقيقة/ثانية)
// -----------------------------------------------------------------------
const TimeUnit = ({ value, label }) => (
  <div className="flex flex-col items-center justify-center rounded-xl bg-[#0A1128]/60 border border-white/10 px-2.5 py-2 min-w-[52px]">
    <span className="text-white font-black text-lg sm:text-xl tabular-nums leading-none">
      {String(value).padStart(2, "0")}
    </span>
    <span className="text-[10px] text-white/50 mt-1">{label}</span>
  </div>
);

// -----------------------------------------------------------------------
// بطاقة الموسم
// -----------------------------------------------------------------------
const SeasonCard = ({ season }) => {
  const status = getSeasonStatus(season);
  const meta = STATUS_META[status];
  const accent = ACCENT_STYLES[season.accent] || ACCENT_STYLES.gold;
  const target = status === "upcoming" ? season.startAt : season.endAt;
  const countdown = useCountdown(target);
  const Icon = ICONS[season.icon] || Trophy;

  return (
    <div
      data-testid={`season-card-${season.id}`}
      className={`relative rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.01] border ${accent.border} ${accent.ring} p-4 sm:p-5 flex flex-col gap-4 overflow-hidden`}
    >
      {/* شريط ملوّن علوي */}
      <div
        className={`absolute top-0 inset-x-0 h-1 bg-gradient-to-l ${accent.stripe}`}
        aria-hidden="true"
      />

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-11 h-11 rounded-xl ${accent.iconBg} flex items-center justify-center shadow-lg flex-shrink-0`}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-white font-black text-base sm:text-lg leading-tight">
              {season.title}
            </div>
            <div className="text-white/50 text-[11px] mt-0.5 tabular-nums" dir="ltr">
              {season.startDateLabel} — {season.endDateLabel}
            </div>
          </div>
        </div>
        <span
          data-testid={`season-badge-${season.id}`}
          className={`inline-flex items-center gap-1 text-[11px] font-black px-2.5 py-1 rounded-full ${accent.badgeBg} shadow flex-shrink-0`}
        >
          Season: {season.seasonLabel}
        </span>
      </div>

      {/* Status pill */}
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${meta.pill}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
          <meta.Icon className="w-3 h-3" />
          {meta.label}
        </span>
      </div>

      {/* Countdown or ended state */}
      {status === "ended" ? (
        <div className="rounded-xl bg-white/[0.02] border border-white/10 px-3 py-4 text-center">
          <div className="text-white/60 text-xs font-bold">
            انتهى هذا الموسم
          </div>
          <div className="text-white/40 text-[11px] mt-1">
            تابعنا لمعرفة تفاصيل الموسم القادم
          </div>
        </div>
      ) : (
        <div>
          <div className="text-white/60 text-[11px] mb-2 flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            {meta.cta}
          </div>
          <div className="grid grid-cols-4 gap-2">
            <TimeUnit value={countdown.days} label="يوم" />
            <TimeUnit value={countdown.hours} label="ساعة" />
            <TimeUnit value={countdown.minutes} label="دقيقة" />
            <TimeUnit value={countdown.seconds} label="ثانية" />
          </div>
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------
// القسم الرئيسي
// -----------------------------------------------------------------------
const SeasonsSection = () => {
  const { seasons } = useEventsData();
  if (!seasons || seasons.length === 0) return null;
  return (
    <section
      id="seasons"
      data-testid="seasons-section"
      className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12"
    >
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C9A96A]/[0.08] border border-[#C9A96A]/25 text-[#D4B98A] text-[11px] font-medium mb-3">
          <Gamepad2 className="w-3.5 h-3.5" />
          <span>Call of Duty Mobile</span>
        </div>
        <h2 className="text-white text-3xl sm:text-4xl font-black tracking-tight">
          مواسم كود موبايل
        </h2>
        <p className="text-white/50 text-sm sm:text-base mt-2 max-w-xl mx-auto">
          تابع مواعيد المواسم الحالية والقادمة مع عدّاد تنازلي مباشر.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {seasons.map((s) => (
          <SeasonCard key={s.id} season={s} />
        ))}
      </div>
    </section>
  );
};

export default SeasonsSection;
